import React, { ReactNode, useMemo, useState } from 'react';

import { FormComponent, UiComponent } from 'components';
import { usePageOption } from 'hooks';
import { UserGroupQuery } from 'queries';
import {
  IUserGroupCandidate,
  IUserGroupListItem,
  IUserGroupTarget,
  SearchUserGroupFilterType,
} from 'types';
import { formatNameWithPhone } from './format';
import * as PickerStyle from './style';

const toCandidate = (t: IUserGroupTarget): IUserGroupCandidate => ({
  id: t.userId,
  name: t.userName,
  phone: t.userPhone,
});

interface Props {
  selectedIds: Set<number>;
  onAdd: (users: IUserGroupCandidate[]) => void;
}

function GroupSearch({ selectedIds, onAdd }: Props) {
  const [group, setGroup] = useState<IUserGroupListItem | null>(null);

  if (!group) return <GroupSelect onSelect={setGroup} />;

  return (
    <>
      <div css={PickerStyle.selectedEvent}>
        <span>
          <strong>{group.name}</strong>
        </span>
        <UiComponent.Button
          variant="border"
          color="primary"
          onClick={() => setGroup(null)}
        >
          그룹 변경
        </UiComponent.Button>
      </div>
      <GroupMembers
        groupId={group.id}
        selectedIds={selectedIds}
        onAdd={onAdd}
      />
    </>
  );
}

const GROUP_COLS: string[] = ['그룹명', '회원 수', '선택'];

function GroupSelect({
  onSelect,
}: {
  onSelect: (group: IUserGroupListItem) => void;
}) {
  const { pageOption, handlePage } = usePageOption(10);
  const [filter, setFilter] = useState<SearchUserGroupFilterType>({});

  const { data } = UserGroupQuery.useGetAll({
    page: pageOption.page,
    size: pageOption.size,
    ...filter,
  });

  const handleSearch = (d: SearchUserGroupFilterType) => {
    setFilter({ name: d.name?.trim() || undefined });
    handlePage({ ...pageOption, page: 1 });
  };

  const renderer: Record<
    (typeof GROUP_COLS)[number],
    (data: IUserGroupListItem & { row: number }) => ReactNode
  > = useMemo(
    () => ({
      그룹명: ({ name }: IUserGroupListItem) => (
        <p css={{ textAlign: 'left' }}>{name}</p>
      ),
      '회원 수': ({ memberCount }: IUserGroupListItem) => (
        <>{memberCount.toLocaleString('ko')}명</>
      ),
      선택: (group: IUserGroupListItem) => (
        <UiComponent.Button
          variant="contained"
          color="primary"
          onClick={() => onSelect(group)}
        >
          선택
        </UiComponent.Button>
      ),
    }),
    [onSelect],
  );

  const rows = (data?.data || []).map((g: IUserGroupListItem) => ({
    ...g,
    rowKey: g.id,
  }));

  return (
    <>
      <FormComponent.Form css={PickerStyle.searchRow} submit={handleSearch}>
        <FormComponent.SearchInput
          name="name"
          placeholder="그룹명으로 검색해주세요"
          css={{ width: '100%' }}
        />
      </FormComponent.Form>
      <div css={PickerStyle.listWrap}>
        <UiComponent.Table
          cols={GROUP_COLS}
          renderer={renderer}
          data={rows}
          sticky
          empty="등록된 회원 그룹이 없습니다"
        />
      </div>
      <UiComponent.Pagination
        pageOption={pageOption}
        handlePage={handlePage}
        totalPage={data?.totalPages || 1}
      />
    </>
  );
}

const MEMBER_COLS: string[] = ['이름', '선택'];

function GroupMembers({
  groupId,
  selectedIds,
  onAdd,
}: Props & { groupId: number }) {
  const { data } = UserGroupQuery.useGetTargets(groupId);
  const targets = data?.data ?? [];

  const renderer: Record<
    (typeof MEMBER_COLS)[number],
    (data: IUserGroupTarget & { row: number }) => ReactNode
  > = useMemo(
    () => ({
      이름: ({ userName, userPhone }: IUserGroupTarget) => (
        <>{formatNameWithPhone(userName, userPhone)}</>
      ),
      선택: (target: IUserGroupTarget) => {
        const isSelected = selectedIds.has(target.userId);
        return (
          <UiComponent.Button
            variant={isSelected ? 'border' : 'contained'}
            color="primary"
            disabled={isSelected}
            onClick={() => onAdd([toCandidate(target)])}
          >
            {isSelected ? '선택됨' : '선택'}
          </UiComponent.Button>
        );
      },
    }),
    [selectedIds, onAdd],
  );

  const rows = targets.map((t) => ({ ...t, rowKey: t.userId }));

  return (
    <>
      <div css={PickerStyle.listWrap}>
        <UiComponent.Table
          cols={MEMBER_COLS}
          renderer={renderer}
          data={rows}
          sticky
          empty="그룹에 속한 회원이 없습니다"
        />
      </div>
      <div css={PickerStyle.bulkRow}>
        <UiComponent.Button
          variant="contained"
          color="primary"
          disabled={targets.length === 0}
          onClick={() => onAdd(targets.map(toCandidate))}
        >
          {`그룹 ${targets.length.toLocaleString('ko')}명 전체 선택`}
        </UiComponent.Button>
      </div>
    </>
  );
}

export default GroupSearch;
